import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Checkbox } from "antd";
import Select from "react-select";

// Services
import httpService from "../../../services/http/user-managment/role.service";
import permissionService from "../../../services/http/user-managment/permission.service";
import notificationService from "../../../services/toaster.service";

// Components
import UnlimitInput from "../../../components/ui-kit/unlimit-input";
import UnlimitToggle from "../../../components/ui-kit/unlimit-toggle";
import UnlimitSubmitButton from "../../../components/ui-kit/unlimit-submit-button";
import UnlimitBreadcrumb from "../../../components/ui-kit/unlimit-breadcrumb";
import UnlimitTable from "../../../components/ui-kit/unlimit-table";
import UnlimitLoading from "../../../components/ui-kit/unlimit-loading";

// Utils & Routes
import { all_routes } from "../../../Router/all_routes";
import { store } from "../../../core/redux/store";

// Constants
const PERMISSION_ACTIONS = ["create", "edit", "delete", "view"];

const RoleForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { t } = useTranslation();

  // Form state
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    setError,
  } = useForm({
    defaultValues: {
      is_active: !id, // Default to active for new roles
    },
  });

  // Component state
  const [isLoading, setIsLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);
  const [permissions, setPermissions] = useState([]);
  const [selectedModules, setSelectedModules] = useState([]);
  const [selectedSchema, setSelectedSchema] = useState();
  const [showFullForm, setShowFullForm] = useState(false);
  const [schemaLoading, setSchemaLoading] = useState(false);
  const [availableSchemas, setAvailableSchemas] = useState([]);

  // Memoized values
  const { auth } = store.getState();
  const roles = auth.roles ?? [];
  const currentUserRoleType = roles[0]?.role_type;

  // Fetch available schemas dynamically from API
  const fetchAvailableSchemas = useCallback(async () => {
    try {
      setSchemaLoading(true);
      const response = await httpService.roleList({});

      if (response.status === 200) {
        const allRoles = response.data.data?.data || [];

        // Extract unique role types/schemas
        let uniqueSchemas = [
          ...new Set(allRoles.map((role) => role.role_type)),
        ].filter(Boolean);

        // Only filter out SA-Admin if current user is not SA-Admin
        // All other users can see all roles except SA-Admin
        if (currentUserRoleType !== "SA-Admin") {
          uniqueSchemas = uniqueSchemas.filter(
            (schema) => schema !== "SA-Admin"
          );
        }

        setAvailableSchemas(uniqueSchemas);
      }
    } catch (error) {
      console.error("Error fetching schemas:", error);
      notificationService.errorMessage(t("role.schemasFetchError"));
    } finally {
      setSchemaLoading(false);
    }
  }, [currentUserRoleType, t]);

  const schemaOptions = useMemo(
    () => availableSchemas.map((s) => ({ value: s, label: s })),
    [availableSchemas]
  );

  const breadcrumbItems = useMemo(
    () => [
      { label: "", link: "/", icon: "feather-home" },
      { label: t("role.rolesAndPermissions"), link: "/role-list" },
      {
        label: id ? t("role.editRole") : t("role.addRole"),
        active: true,
      },
    ],
    [id, t]
  );

  // API calls
  const fetchPermissions = useCallback(
    async (schemaValue) => {
      if (!schemaValue) return;

      try {
        const response = await permissionService.permissionList({
          page: 1,
          per_page: 5000,
          scema: schemaValue,
        });

        if (response.status === 200) {
          hydratePermissions(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching permissions:", error);
        notificationService.errorMessage(t("role.permissionsFetchError"));
      }
    },
    [t]
  );

  const fetchRole = useCallback(async () => {
    if (!id) return;

    try {
      const response = await httpService.getRole(id);
      if (response?.status === 200) {
        const data = response.data.data;
        setValue("name", data.name);
        setValue("is_active", data.is_active === 1);
        setShowFullForm(true);
        setSelectedModules(data.permissions?.map((item) => item.id) ?? []);
        setSelectedSchema(data.role_type);


        // Fetch permissions after setting the schema
        if (data.role_type) {
          await fetchPermissions(data.role_type);
        }
      }
    } catch (error) {
      console.error("Error fetching role:", error);
      notificationService.errorMessage(t("role.fetchError"));
    }
  }, [id, setValue, t, fetchPermissions]);

  const hydratePermissions = useCallback((data) => {
    const hydratedPermissions = Object.entries(data).map(
      ([moduleName, modulePermissions]) => {
        //console.log("Action ", modulePermissions);
        const findPermissionId = (action) => {
          return (
            modulePermissions.find(
              (item) =>
                item.name.includes(action) ||
                (action == "create" && item.name.includes("add"))
            )?.id ?? -1
          );
        };

        return {
          module: moduleName,
          ids: PERMISSION_ACTIONS.map(findPermissionId),
        };
      }
    );

    setPermissions(hydratedPermissions);
  }, []);

  // Event handlers
  const handleSchemaChange = useCallback(
    (selectedOption) => {
      setSelectedModules([]);
      setSelectedSchema(selectedOption.value);
      fetchPermissions(selectedOption.value);
    },
    [fetchPermissions]
  );

  const handleModuleSelection = useCallback((moduleId) => {
    setSelectedModules((prev) =>
      prev.includes(moduleId)
        ? prev.filter((id) => id !== moduleId)
        : [...prev, moduleId]
    );
  }, []);

  const handleAllowAll = useCallback(
    (moduleIds) => {
      const validIds = moduleIds.filter((id) => id !== -1);
      const allSelected = validIds.every((id) => selectedModules.includes(id));

      console.log("allSelected", allSelected);

      setSelectedModules((prev) =>
        allSelected
          ? prev.filter((id) => !validIds.includes(id))
          : [...new Set([...prev, ...validIds])]
      );
    },
    [selectedModules]
  );

  const handleAssignButtonClick = useCallback((e) => {
    e.preventDefault();
    setShowFullForm(true);
  }, []);

  // Form submission
  const createRole = useCallback(
    async (formData) => {
      try {
        const response = await httpService.createRole(formData);
        if (response?.status === 200) {
          notificationService.successMessage(t("role.roleCreateSuccess"));
          setTimeout(() => navigate(all_routes.roleList), 500);
        }
      } catch (error) {
        console.error("Error creating role:", error);
        if (error.validation_error?.errors) {
          // Handle validation errors if needed
        }
      }
    },
    [navigate, t]
  );

  const updateRole = useCallback(
    async (formData) => {
      try {
        const response = await httpService.updateRole(formData);
        if (response?.status === 200) {
          notificationService.successMessage(t("role.roleUpdateSuccess"));
          setTimeout(() => navigate(all_routes.roleList), 1000);
        }
      } catch (error) {
        console.error("Error updating role:", error);
        if (error.validation_error?.errors) {
          // Handle validation errors if needed
        }
      }
    },
    [navigate, t]
  );

  const onSubmitForm = useCallback(
    async (data) => {
      try {
        setIsLoading(true);
        const formData = new FormData();

        formData.append("name", data.name);
        formData.append("guard_name", "web");
        formData.append("scema", selectedSchema);
        formData.append("is_active", data.is_active ? 1 : 0);

        selectedModules.forEach((mod, index) => {
          formData.append(`permissions[${index}]`, mod);
        });

        if (id) {
          formData.append("id", id);
          await updateRole(formData);
        } else {
          await createRole(formData);
        }
      } finally {
        setIsLoading(false);
      }
    },
    [selectedModules, selectedSchema, id, createRole, updateRole]
  );

  // Table columns
  const permissionColumns = useMemo(
    () => [
      {
        title: t("tableColumnName.srNo"),
        dataIndex: "index",
        align: "center",
        render: (_, __, index) => index + 1,
      },
      {
        title: t("tableColumnName.module"),
        dataIndex: "module",
      },
      ...PERMISSION_ACTIONS.map((action, index) => ({
        title: t(`general.actions.${action}`),
        render: (_, record) => (
          <div key={`${record.module}-${action}`}>
            {record.ids[index] !== -1 && (
              <Checkbox
                checked={selectedModules.includes(record.ids[index])}
                onChange={() => handleModuleSelection(record.ids[index])}
              />
            )}
          </div>
        ),
      })),
      {
        title: t("general.actions.allowAll"),
        render: (_, record) => {
          const validIds = record.ids.filter((id) => id !== -1);
          return validIds.length > 0 ? (
            <Checkbox
              checked={validIds.every((id) => selectedModules.includes(id))}
              onChange={() => handleAllowAll(record.ids)}
            />
          ) : null;
        },
      },
    ],
    [t, selectedModules, handleModuleSelection, handleAllowAll]
  );

  // Effects
  useEffect(() => {
    const initializeData = async () => {
      setPageLoading(true);
      try {
        await fetchAvailableSchemas();
        await fetchRole();
      } finally {
        setPageLoading(false);
      }
    };

    initializeData();
  }, [fetchAvailableSchemas, fetchRole]);

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex flex-column">
            <div className="page-title">
              <h3 className="text-black">{t("role.rolesAndPermissions")}</h3>
            </div>
            <UnlimitBreadcrumb items={breadcrumbItems} />
          </div>
        </div>

        <form onSubmit={handleSubmit(onSubmitForm)} noValidate>
          <div className="card">
            {pageLoading ? (
              <div className="row d-flex justify-content-center mt-2">
                <UnlimitLoading width={50} height={50} />
              </div>
            ) : (
              <div className="card-body add-product pb-0">
                <div className="row align-items-start">
                  <div className="col-lg-3 col-sm-4 col-12">
                    <div className="unlimit-uikit-select">
                      <label className="mb-2">{t("role.permSchema")}</label>
                      <Select
                        options={schemaOptions}
                        className="select custom-select"
                        placeholder={
                          schemaLoading
                            ? "Loading schemas..."
                            : t("role.selectSchema")
                        }
                        value={schemaOptions.find(
                          (option) => option.value === selectedSchema
                        )}
                        onChange={handleSchemaChange}
                        isDisabled={pageLoading || schemaLoading}
                        isLoading={schemaLoading}
                      />
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="input-blocks summer-description-box transfer mb-3">
                      <UnlimitInput
                        label={t("inputField.label.roleName")}
                        id="name"
                        type="text"
                        placeholder={t("inputField.placeholder.enterRoleName")}
                        register={register}
                        rules={{
                          required: t("inputField.validation.required"),
                        }}
                        error={errors.name}
                        errorMessage={errors.name?.message}
                      />
                    </div>
                  </div>

                  <div className="col-md-3 ps-2 mt-4 pt-2">
                    <UnlimitToggle
                      isChecked={!id}
                      label={t("tableColumnName.status")}
                      id="is_active"
                      register={register}
                    />
                  </div>
                </div>

                {showFullForm && (
                  <>
                    <h4 className="text-black f-bold bold mb-3">
                      {t("role.permissions")}
                    </h4>
                    <div className="table-responsive">
                      <UnlimitTable
                        columns={permissionColumns}
                        dataSource={permissions}
                        rowKey="module"
                        pagination={false}
                      />
                    </div>
                  </>
                )}

                {!showFullForm && (
                  <button
                    type="button"
                    className="btn btn-submit mb-3"
                    onClick={handleAssignButtonClick}
                  >
                    {t("role.assignPermissions")}
                  </button>
                )}

                {showFullForm && (
                  <div className="col-lg-12 my-4">
                    <Link to={all_routes.roleList}>
                      <button type="button" className="btn btn-cancel me-2">
                        {t("general.button.cancel")}
                      </button>
                    </Link>
                    <UnlimitSubmitButton
                      label={
                        id
                          ? t("general.button.update")
                          : t("general.button.submit")
                      }
                      isLoading={isLoading}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default RoleForm;
